import type { ReactElement } from 'react';
import { Star, Box, Sparkles, ImageIcon, Layers, CircleDot } from 'lucide-react';

import type { AvatarItem } from '@/services/avatarApi';
import { cn } from '@/lib/cn';

interface AvatarBadgesProps {
  avatar: AvatarItem;
  /**
   * `inline` = small badges meant to sit inside a card overlay (default).
   * `compact` = even smaller, single icon-only row for grid tiles.
   */
  size?: 'inline' | 'compact';
  className?: string;
}

/**
 * Derives the set of state flags from an AvatarItem. Surfaced as an exported
 * helper so the parent list can pre-compute counts (e.g. "5 3D · 2 animated").
 */
export function deriveAvatarFlags(avatar: AvatarItem) {
  const meshStatus = avatar.mesh?.status ?? 'none';
  const packs = avatar.mesh?.animationPacks ?? [];
  const readyPacks = packs.filter((p) => p.status === 'ready');
  const inFlightMesh =
    meshStatus !== 'none' &&
    meshStatus !== 'ready' &&
    meshStatus !== 'failed' &&
    meshStatus !== 'animated_ready' &&
    meshStatus !== 'animated_failed';
  const has3d = meshStatus === 'ready' || meshStatus === 'animated_ready' || meshStatus === 'animated_failed';
  const hasAnimation = readyPacks.length > 0 || meshStatus === 'animated_ready';
  return {
    isFeatured: !!avatar.isFeatured,
    has3d,
    inFlightMesh,
    hasAnimation,
    readyPackCount: readyPacks.length,
    isPhotoBase: !!avatar.sourceImageUrl,
    seriesCount: avatar.seriesCount ?? 1,
  };
}

export function AvatarBadges({ avatar, size = 'inline', className }: AvatarBadgesProps) {
  const flags = deriveAvatarFlags(avatar);

  const badges: Array<{ key: string; icon: ReactElement; label: string; tone: string }> = [];

  if (flags.isFeatured) {
    badges.push({
      key: 'featured',
      icon: <Star aria-hidden className="h-3 w-3 fill-current" />,
      label: 'Hub',
      tone: 'bg-amber-400 text-black',
    });
  }
  if (flags.inFlightMesh) {
    badges.push({
      key: 'inflight',
      icon: <CircleDot aria-hidden className="h-3 w-3 animate-pulse" />,
      label: '…',
      tone: 'bg-brand/80 text-brand-fg',
    });
  } else if (flags.has3d) {
    badges.push({
      key: '3d',
      icon: <Box aria-hidden className="h-3 w-3" />,
      label: '3D',
      tone: 'bg-fg/15 text-fg',
    });
  }
  if (flags.hasAnimation) {
    badges.push({
      key: 'anim',
      icon: <Sparkles aria-hidden className="h-3 w-3" />,
      // Show pack count only when >1 (multi-pack), otherwise just the icon.
      label: flags.readyPackCount > 1 ? String(flags.readyPackCount) : '',
      tone: 'bg-brand/20 text-brand',
    });
  }
  if (flags.isPhotoBase) {
    badges.push({
      key: 'photo',
      icon: <ImageIcon aria-hidden className="h-3 w-3" />,
      label: '',
      tone: 'bg-fg/15 text-fg',
    });
  }
  if (flags.seriesCount > 1) {
    badges.push({
      key: 'series',
      icon: <Layers aria-hidden className="h-3 w-3" />,
      label: String(flags.seriesCount),
      tone: 'bg-fg/15 text-fg',
    });
  }

  if (badges.length === 0) return null;

  const pillBase =
    size === 'compact'
      ? 'flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold leading-none'
      : 'flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold leading-none';

  return (
    <div className={cn('flex flex-wrap items-center gap-1', className)}>
      {badges.map((b) => (
        <span key={b.key} className={cn(pillBase, b.tone)}>
          {b.icon}
          {b.label && <span>{b.label}</span>}
        </span>
      ))}
    </div>
  );
}
